import { useState, useCallback, useEffect } from 'react';
import { getFavorites, addFavorite, removeFavorite, isFavorite } from '../services/storage';
import { Favorite, ProductListing } from '../types';

export function useFavorites() {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFavorites();
      setFavorites(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch favorites');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleFavorite = useCallback(
    async (product: ProductListing) => {
      try {
        const isFav = await isFavorite(product.id);

        if (isFav) {
          await removeFavorite(product.id);
          setFavorites((prev) => prev.filter((fav) => fav.product_id !== product.id));
        } else {
          await addFavorite(product);
          setFavorites((prev) => [
            {
              id: Math.random().toString(),
              user_id: '',
              product_id: product.id,
              title: product.title,
              price: product.price,
              image_url: product.images?.[0] || null,
              marketplace: product.marketplace,
              url: product.url,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
            ...prev,
          ]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to toggle favorite');
      }
    },
    []
  );

  const checkIsFavorite = useCallback(async (productId: string): Promise<boolean> => {
    try {
      return await isFavorite(productId);
    } catch (err) {
      console.error('Failed to check favorite:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  return {
    favorites,
    loading,
    error,
    toggleFavorite,
    checkIsFavorite,
    refresh: fetchFavorites,
  };
}